import * as React from 'react'
import { styled, css } from 'next-yak'

import * as bp from '../../../../constants/breakpoints'
import * as accounting from '../../../../core/accounting'
import * as carousel from '../../../../styles/carousel'
import * as rewardCard from '../../../../styles/card/reward'

import { PagedCarousel } from '../../../carousel/PagedCarousel'

import * as type from '../type'

type RewardCarouselProps = {
  count?: number
  currency: string
  locale: string
  reward: type.RewardCardProps['reward']
  onCountChange?: React.Dispatch<React.SetStateAction<number>>
  isNextDisabled?: boolean
  isPrevDisabled?: boolean
}

function ControlledRewardCarousel({
  count,
  currency,
  locale,
  onCountChange,
  reward,
  isNextDisabled,
  isPrevDisabled,
}: RewardCarouselProps & {
  count: number
  onCountChange: (newCount: number) => void
}): React.ReactElement<any> {
  // NOTE: from all option values, we only keep the ones with an image
  const allOptionValuesWithImage = reward.options
    ?.flatMap((option) => option.values)
    ?.filter((optionValue) => optionValue.imageURL !== undefined)

  // then we format the labels of those option values with an image
  const optionValuesWithImageLabels = getOptionValuesWithImageLabels(reward, allOptionValuesWithImage)

  const carouselLabels = getCarouselLabels(reward, optionValuesWithImageLabels)
  const carouselImgs = getCarouselImgs(reward, allOptionValuesWithImage)

  const isNotCarousel = carouselImgs.length === 1

  const lastCount = React.useRef(-1)
  React.useEffect(() => {
    if (count !== lastCount.current) {
      lastCount.current = count
      if (carouselImgs[count + 1]) {
        const img = new Image()
        img.src = carouselImgs[count + 1] as string
      }
    }
  }, [count])

  const carouselRef = React.useRef<HTMLDivElement>(null)

  return (
    <CarouselWrapper $hideButtons={isNotCarousel} ref={carouselRef}>
      <StyledPagedCarousel
        count={count}
        $isVariantImage={count > 0}
        isNextDisabled={isNextDisabled}
        isPrevDisabled={isPrevDisabled}
        $minHeight={carouselImgs.length > 1 ? 230 : 0}
        onCountChange={(newCount) => {
          onCountChange && onCountChange(newCount)
        }}
        labels={carouselLabels}
        sliderButtonSize="small"
      >
        {carouselImgs.map((imgUrl) => (
          <rewardCard.CardImage key={imgUrl} src={imgUrl} />
        ))}
      </StyledPagedCarousel>
    </CarouselWrapper>
  )

  function getOptionValuesWithImageLabels(
    reward: type.RewardCardProps['reward'],
    allOptionValuesWithImage: type.RewardOption['values'] | undefined,
  ): string[] | undefined {
    return allOptionValuesWithImage?.map((optionValue) => {
      const parentRewardOption = reward.options?.find((option) => option.values.includes(optionValue))

      const formattedAdditionalCost = optionValue.additionalCost
        ? accounting.localeMoney(optionValue.additionalCost, {
            currency: currency,
            lang: locale,
          })
        : undefined

      return `${parentRewardOption?.name}: ${optionValue.title} ${
        formattedAdditionalCost ? `(+${formattedAdditionalCost})` : ''
      }`
    })
  }

  function getCarouselLabels(
    reward: type.RewardCardProps['reward'],
    optionValuesWithImageLabels: string[] | undefined,
  ): string[] {
    if (reward.img) {
      return ['', ...(optionValuesWithImageLabels ?? [])]
    }
    return [...(optionValuesWithImageLabels ?? [])]
  }

  function getCarouselImgs(
    reward: type.RewardCardProps['reward'],
    allOptionValuesWithImage: type.RewardOption['values'] | undefined,
  ): (string | undefined)[] {
    if (reward.options === undefined) {
      if (reward.img) {
        return [reward.img]
      }
      return []
    }

    const optionValuesImgsUrls = allOptionValuesWithImage?.map((optionValue) => optionValue.imageURL) ?? []

    if (reward.img) {
      return [reward.img, ...optionValuesImgsUrls]
    }

    return [...optionValuesImgsUrls]
  }
}

function UncontrolledRewardCarousel(props: RewardCarouselProps & { initialCount?: number }): React.ReactElement<any> {
  const [count, setCount] = React.useState(props.initialCount ?? 0)

  const allOptionValuesWithImage = props.reward.options
    ?.flatMap((option) => option.values)
    ?.filter((optionValue) => optionValue.imageURL !== undefined)

  return (
    <ControlledRewardCarousel
      count={count}
      onCountChange={setCount}
      isPrevDisabled={count === 0}
      isNextDisabled={
        props.reward.img
          ? count === (allOptionValuesWithImage?.length ?? 0)
          : count === (allOptionValuesWithImage?.length ?? 0) - 1
      }
      {...props}
    />
  )
}

export function RewardCarousel({ count, onCountChange, ...props }: RewardCarouselProps): React.ReactElement<any> {
  if (count !== undefined && onCountChange !== undefined) {
    return <ControlledRewardCarousel {...props} count={count} onCountChange={onCountChange} />
  } else {
    return <UncontrolledRewardCarousel {...props} />
  }
}

const StyledPagedCarousel = styled(PagedCarousel)<{ $isVariantImage: boolean; $minHeight: number }>`
  min-height: ${({ $minHeight }) => $minHeight}px;
  overflow: hidden;
  width: 100%;

  ${carousel.PagedSlide} {
    display: flex;
  }

  @media screen and (${bp.LAPTOP}) {
    ${carousel.PagedLabel} {
      opacity: 0;
    }

    &:hover {
      ${carousel.PagedLabel} {
        opacity: 1;
      }
    }
  }

  ${carousel.PagedControlNext} {
    right: 16px;
    transition-duration: 200ms;
  }

  ${carousel.PagedLabel} {
    transition-duration: 200ms;
  }

  ${carousel.PagedControlPrev} {
    left: 16px;
    transition-duration: 200ms;
  }

  ${({ $isVariantImage }) => {
    if ($isVariantImage) {
      return css`
        ${rewardCard.CardImage} {
          object-fit: contain;
          max-height: 500px;
        }
      `
    }
  }};
`

const CarouselWrapper = styled.div<{ $hideButtons: boolean }>`
  display: flex;
  width: 100%;
  height: 100%;
  position: relative;

  ${({ $hideButtons }) => {
    if ($hideButtons) {
      return css`
        ${carousel.PagedControlNext} {
          display: none;
        }

        ${carousel.PagedControlPrev} {
          display: none;
        }
      `
    }
  }};
`
